import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Braces } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import type { WildcardContext } from '../../lib/publishing/captionWildcards';
import {
  buildWildcardMenu,
  wildcardMenuState,
  type WildcardInsertMode,
} from '../../lib/publishing/wildcardInsert';

// Publishing composer §B — the caption toolbar's "Insert wildcard" menu (item 4,
// 2026-07-28). Mirrors the broadcast SlugPicker pattern: click an entry →
// onInsert(text) at the caption's caret, so AMs don't memorise brace syntax.
//
// WHAT gets inserted depends on the composer (2026-08-05) — see
// src/lib/publishing/wildcardInsert.ts for the full reasoning:
//   mode='token'     the `{token}`, resolved per account at dispatch (multi).
//   mode='resolved'  the selected account's REAL text, editable in place (single).

export function WildcardMenu({
  mode,
  context,
  accountId,
  onInsert,
  disabled,
}: {
  mode: WildcardInsertMode;
  /** The selected account's resolved wildcard values. Required by 'resolved'. */
  context: WildcardContext | null;
  /** The selected account, for the "set it up" deep link. '' / null = none. */
  accountId?: string | null;
  onInsert: (text: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const state = wildcardMenuState(mode, context, !!accountId);
  const items = buildWildcardMenu(mode, context);
  const blocked = disabled || state.disabled;

  const pick = (text: string) => {
    onInsert(text);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1">
      <Popover open={open && !blocked} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={blocked}
            aria-label="Insert wildcard"
            title={state.reason ?? 'Insert a merge field'}
            className="h-7 gap-1 px-2 text-slate-500 hover:text-slate-700"
          >
            <Braces className="h-4 w-4" />
            Wildcard
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-1">
          <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {mode === 'resolved' ? "Insert this account's details" : 'Insert a merge field'}
          </p>
          {items.map((item) =>
            item.insertText === null ? (
              // Unavailable — say WHY, and point at where to fix it. Inserting an
              // empty string here would leave nothing on screen to explain itself.
              <div key={item.def.token} className="rounded px-2 py-1 text-left opacity-70">
                <span className="text-xs text-slate-500">{item.def.label}</span>
                <span className="block text-xs text-slate-400">{item.unavailableReason}</span>
                {accountId ? (
                  <Link
                    to={`/accounts/${accountId}?tab=services`}
                    className="text-xs text-kyck-700 underline"
                    onClick={() => setOpen(false)}
                  >
                    Set it up on this account
                  </Link>
                ) : null}
              </div>
            ) : (
              <button
                key={item.def.token}
                type="button"
                onClick={() => pick(item.insertText!)}
                className="block w-full rounded px-2 py-1 text-left hover:bg-slate-50"
              >
                {mode === 'resolved' ? (
                  <>
                    <span className="text-xs font-medium text-slate-900">{item.def.label}</span>
                    <span className="block truncate text-xs text-slate-400">
                      {item.insertText}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-mono text-xs text-slate-900">{`{${item.def.token}}`}</span>
                    <span className="ml-2 text-xs text-slate-500">{item.def.label}</span>
                    <span className="block truncate text-xs text-slate-400">
                      {item.def.description}
                    </span>
                  </>
                )}
              </button>
            ),
          )}
          <p className="px-2 py-1 text-[10px] text-slate-400">
            {mode === 'resolved'
              ? "Inserted as text you can edit — it won't change if the account does."
              : 'Resolved per account when the post is sent.'}
          </p>
        </PopoverContent>
      </Popover>
      {/* The reason is rendered, not just tooltipped: a disabled button with no
          explanation reads as broken. */}
      {state.reason && !disabled && (
        <span className="text-[11px] text-slate-400">{state.reason}</span>
      )}
    </div>
  );
}
